import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { getBaseUrl } from '@services/api';

interface TrackActionData {
  type: 'url_button_click' | 'file_download' | 'file_download_click';
  url?: string;
}

const trackGameAction = async (data: TrackActionData, gameBuildId: string | null) => {
  try {
    if (!gameBuildId) {
      console.warn('⚠️ gameBuildId не найден, пропускаем отслеживание действия');
      return;
    }

    await axios.post(`${getBaseUrl()}/public/games/track-action`, {
      gameBuildId,
      type: data.type,
      url: data.url
    }, {
      withCredentials: true
    });

    console.log('📊 Отслежено действие:', data.type, data.url || '');
  } catch (error) {
    console.error('Ошибка при отправке аналитики:', error);
  }
};

export const GameActionTracker: React.FC = () => {
  const [searchParams] = useSearchParams();
  const gameBuildId = searchParams.get('buildId') || searchParams.get('gameBuildId');

  useEffect(() => {
    // Сохраняем оригинальные функции
    const originalWindowOpen = window.open;
    const originalCreateElement = document.createElement;

    // 1. Перехватываем window.open (для Unity и HTML игр)
    window.open = function(url?: string | URL, target?: string, features?: string) {
      if (url) {
        const urlString = typeof url === 'string' ? url : url.toString();

        // Проверяем, не является ли это внутренним URL игры
        if (!urlString.startsWith('blob:') && !urlString.startsWith('data:')) {
          console.log('🔗 Открытие URL:', urlString);

          trackGameAction(
            {
              type: 'url_button_click',
              url: urlString
            },
            gameBuildId
          );
        }
      }

      return originalWindowOpen.call(window, url, target, features);
    };

    // 2. Перехватываем создание элементов <a> для скачивания
    document.createElement = function(tagName: string, options?: ElementCreationOptions) {
      const element = originalCreateElement.call(document, tagName, options);

      if (tagName.toLowerCase() === 'a') {
        const link = element as HTMLAnchorElement;

        // Отслеживаем установку download атрибута
        const originalSetAttribute = link.setAttribute.bind(link);
        link.setAttribute = function(name: string, value: string) {
          originalSetAttribute(name, value);

          if (name === 'download' && link.href && !link.href.startsWith('blob:')) {
            console.log('📥 Создана ссылка для скачивания:', link.href);

            trackGameAction(
              {
                type: 'file_download',
                url: link.href
              },
              gameBuildId
            );
          }
        };

        // Отслеживаем клик на ссылку с download
        const handleClick = () => {
          if (link.download !== undefined && link.href && !link.href.startsWith('blob:')) {
            console.log('📥 Клик по ссылке скачивания:', link.href);

            trackGameAction(
              {
                type: 'file_download_click',
                url: link.href
              },
              gameBuildId
            );
          }
        };

        link.addEventListener('click', handleClick);
      }

      return element;
    };

    // 3. MutationObserver для отслеживания ссылок, созданных после монтирования
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            // Проверяем прямые ссылки
            if (element.tagName === 'A') {
              const link = element as HTMLAnchorElement;
              if (link.hasAttribute('download') && link.href && !link.href.startsWith('blob:')) {
                console.log('📥 Обнаружена ссылка для скачивания (DOM):', link.href);

                trackGameAction(
                  {
                    type: 'file_download',
                    url: link.href
                  },
                  gameBuildId
                );

                // Отслеживаем клик на эту ссылку
                link.addEventListener('click', () => {
                  trackGameAction(
                    {
                      type: 'file_download_click',
                      url: link.href
                    },
                    gameBuildId
                  );
                });
              }
            }

            // Проверяем вложенные ссылки
            const links = element.querySelectorAll?.('a[download]');
            links?.forEach((link) => {
              const anchor = link as HTMLAnchorElement;
              if (anchor.href && !anchor.href.startsWith('blob:')) {
                console.log('📥 Обнаружена вложенная ссылка для скачивания:', anchor.href);

                trackGameAction(
                  {
                    type: 'file_download',
                    url: anchor.href
                  },
                  gameBuildId
                );

                // Отслеживаем клик на эту ссылку
                anchor.addEventListener('click', () => {
                  trackGameAction(
                    {
                      type: 'file_download_click',
                      url: anchor.href
                    },
                    gameBuildId
                  );
                });
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // 4. Отслеживание событий Unity (если Unity отправляет кастомные события)
    const handleUnityEvent = (e: CustomEvent) => {
      const url = e.detail?.url;
      if (url) {
        console.log('🎮 Unity событие:', url);

        trackGameAction(
          {
            type: 'url_button_click',
            url: url
          },
          gameBuildId
        );
      }
    };

    window.addEventListener('UnityOpenURL', handleUnityEvent as EventListener);

    // Очистка при размонтировании
    return () => {
      window.open = originalWindowOpen;
      document.createElement = originalCreateElement;
      observer.disconnect();
      window.removeEventListener('UnityOpenURL', handleUnityEvent as EventListener);
    };
  }, [gameBuildId]);

  return null; // Компонент не рендерит ничего
};

